import { type ApiMetadata } from '@webapp/core/interfaces/api/ApiMetadata'
import { type ApiResponse } from '@webapp/core/interfaces/api/ApiResponse'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IMileageRatePresetRepository } from '@webapp/modules/expense/business-logic/interfaces/IMileageRatePresetRepository'
import {
  type CreateMileageRatePresetInputData,
  type UpdateExpenseMileageConfigurationInputData,
  type UpdateMileageRatePresetInputData,
} from '@webapp/modules/expense/business-logic/types'
import { type ExpenseMileageConfigurationConstructor } from '@webapp/modules/expense/entities/ExpenseMileageConfiguration'
import { type MileageRatePresetConstructor } from '@webapp/modules/expense/entities/MileageRatePreset'
import { type ExpenseMileageConfigurationApiModel } from '../../api-models/ExpenseMileageConfigurationApiModel'
import { type MileageRatePresetApiModel } from '../../api-models/MileageRatePresetApiModel'
import { mapCreateMileageRatePresetInputDataToApi } from '../../mappers/mapCreateMileageRatePresetInputDataToApi'
import { mapExpenseMileageConfigurationApiModel } from '../../mappers/mapExpenseMileageConfigurationApiModel'
import { mapMileageRatePresetApiModel } from '../../mappers/mapMileageRatePresetApiModel'
import { mapUpdateExpenseMileageConfigurationInputDataToApi } from '../../mappers/mapUpdateExpenseMileageConfigurationInputDataToApi'
import { mapUpdateMileageRatePresetInputDataToApi } from '../../mappers/mapUpdateMileageRatePresetInputDataToApi'
import { type ExpenseMileageConfigurationPayload } from '../../types/ExpenseMileageConfigurationPayload'
import { type MileageRatePresetPayload } from '../../types/MileageRatePresetPayload'

interface Dependency {
  httpClient: IHttpClient
}

export class MileageRatePresetRepository implements IMileageRatePresetRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<MileageRatePresetConstructor>> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<MileageRatePresetApiModel[], ApiMetadata>
      >('/api/v3/settings/expense-mileage-preset/', {
        params: { ...formatRepoFiltersToApiFilters(params) },
      })

      const metadata = res.data.metadata

      return {
        data: res.data.data.map(mapMileageRatePresetApiModel),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getMileageRatePreset(
    id: number
  ): Promise<RepoStandardResponse<MileageRatePresetConstructor>> {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<MileageRatePresetApiModel>
      >(`/api/v3/settings/expense-mileage-preset/${id}/`)
      return { data: mapMileageRatePresetApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async createMileageRatePreset(
    payload: CreateMileageRatePresetInputData
  ): Promise<RepoStandardResponse<MileageRatePresetConstructor>> {
    const mappedPayload = mapCreateMileageRatePresetInputDataToApi(payload)
    try {
      const res = await this.deps.httpClient.post<
        ApiResponse<MileageRatePresetApiModel>,
        MileageRatePresetPayload
      >('/api/v3/settings/expense-mileage-preset/', mappedPayload)
      return { data: mapMileageRatePresetApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateMileageRatePreset(
    id: number,
    payload: UpdateMileageRatePresetInputData
  ): Promise<RepoStandardResponse<MileageRatePresetConstructor>> {
    const mappedPayload = mapUpdateMileageRatePresetInputDataToApi(payload)
    try {
      const res = await this.deps.httpClient.put<
        ApiResponse<MileageRatePresetApiModel>,
        MileageRatePresetPayload
      >(`/api/v3/settings/expense-mileage-preset/${id}/`, mappedPayload)
      return { data: mapMileageRatePresetApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async deleteMileageRatePreset(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(
        `/api/v3/settings/expense-mileage-preset/${id}/`
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getExpenseMileageConfiguration(): Promise<
    RepoStandardResponse<ExpenseMileageConfigurationConstructor>
  > {
    try {
      const res = await this.deps.httpClient.get<
        ApiResponse<ExpenseMileageConfigurationApiModel>
      >('/api/v3/expense/mileage-configuration/')
      return { data: mapExpenseMileageConfigurationApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateExpenseMileageConfiguration(
    input: UpdateExpenseMileageConfigurationInputData
  ): Promise<RepoStandardResponse<ExpenseMileageConfigurationConstructor>> {
    const payload = mapUpdateExpenseMileageConfigurationInputDataToApi(input)
    try {
      const res = await this.deps.httpClient.put<
        ApiResponse<ExpenseMileageConfigurationApiModel>,
        ExpenseMileageConfigurationPayload
      >('/api/v3/expense/mileage-configuration/', payload)
      return { data: mapExpenseMileageConfigurationApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
